import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { Helmet } from "react-helmet";
import Nav from "./Nav";
import Footer from "./Footer/Footer";
import { getImage, getSrc, GatsbyImage } from "gatsby-plugin-image";
import Main from "./Main";
import JSONLDTag from "./jsonld/JSONLDTag";
import JSONLDTagOrganization from "./jsonld/JSONLDTagOrganization";

import OneTrustShowSettings from "./OneTrustShowSettings";
import JSONLDBrand from "./jsonld/JSONLDBrand";
import InternalLink from "./InternalLink";
import LazyLoader from "./LazyLoader";
import CustomHeadElements from "./CustomHeadElements";
import AnalyticsDataLayer from "./AnalyticsDataLayer";

import "../css/layout.css";
import Banner from "./Banner";
import { graphql, useStaticQuery } from "gatsby";
import Favicon from "./Favicon";
import { trackCustomEvent } from "../utilities/analytics";
import FacebookDomainVerification from "./FacebookDomainVerification";
import { SkipNav } from "./SkipNav";

require(`../css/themes/${process.env.GATSBY_SITE_DOMAIN}.css`);

export const LazyAdChoices = () => import("./AdChoices");
export const LazyAdobeAnalytics = () => import("./AdobeAnalytics");
export const LazyCookieConsentAsync = () => import("./CookieConsentAsync");
export const LazyYandexAnalytics = () => import("./YandexAnalytics");
export const LazyVKAnalytics = () => import("./VKAnalytics");
export const LazyMyTargetAnalytics = () => import("./MyTargetAnalytics");
export const LazyDV360Analytics = () => import("./DV360Analytics");
export const LazyGlobalFacebookTag = () => import("./GlobalFacebookTag");
export const LazyCustomPixels = () => import("./CustomPixels");
export const LazyHotJar = () => import("./HotJar");

const STATIC_QUERY = graphql`
  query LayoutQuery {
    homeLink: home {
      path
      lexicon {
        home
      }
    }
  }
`;

const Layout = ({
  absolutePath,
  alternatives,
  children,
  className,
  home,
  metaDescription,
  pageTitle,
  pageType,
  shareImgSrc,
  traits,
  channel,
}) => {
  const { homeLink } = useStaticQuery(STATIC_QUERY);

  const [unscrollable, setUnscrollable] = useState(false);

  const trackHamburgerClickedOpen = () => {
    trackCustomEvent({
      category: "Engagement",
      action: "Click",
      label: "Hamburger Icon (Open)",
    });
  };

  const { brand, domain, socialChannels } = home;
  if (metaDescription.length > 154) {
    metaDescription = metaDescription.slice(0, 154) + "…";
  }

  const navToggleRef = useRef();
  return (
    <>
      <SkipNav contentId="main-content" label={home.lexicon?.skipToContent} />
      <div className="overflow-hidden">
        <Helmet>
          <body className={cx({ unscrollable })} />
        </Helmet>
        <Helmet defaultTitle={brand.name} titleTemplate={`%s | ${brand.name}`}>
          <html
            lang={domain.lang}
            className={cx(pageType, {
              [domain.path.slice(1)]: domain.path !== "/",
            })}
          />
          <title>{pageTitle}</title>
          <meta name="description" content={metaDescription} />
          <meta http-equiv="content-language" content={domain.lang} />
          <meta property="fb:pages" content="350316462033370" />
          <meta property="og:url" content={absolutePath} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:site_name" content={brand.name} />
          <meta property="og:type" content="website" />
          <meta property="og:description" content={metaDescription} />
          {shareImgSrc && <meta property="og:image" content={shareImgSrc} />}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:url" content={absolutePath} />
          <meta name="twitter:title" content={pageTitle} />
          <meta name="twitter:description" content={metaDescription} />
          {shareImgSrc && <meta name="twitter:image" content={shareImgSrc} />}
          {alternatives &&
            alternatives.map(({ href, market }) => (
              <link
                key={`${market}${href}`}
                rel="alternate"
                hreflang={market}
                href={href}
              />
            ))}
          <link rel="canonical" href={absolutePath} />
        </Helmet>
        <CustomHeadElements />
        <Favicon />
        <FacebookDomainVerification />
        <Helmet>
          {domain.hasVersatag && (
            <script>{`
          (function(e, s, t) { t = t || {}, t.id = e, versaTagObj = { $: [], onready: function(e) { this.$.push(e) } }; var n = s.getElementsByTagName("script")[0], r = s.createElement("script"); r.options = t, r.async = !0, r.src = "https://secure-ds.serving-sys.com/SemiCachedScripts/ebOneTag.js?id=" + e, r.options = t, n.parentNode.insertBefore(r, n) })("2222", document, { dataLayer:"sz_datalayer" });
`}</script>
          )}
        </Helmet>
        {domain.adobeConfig && (
          <LazyLoader
            dynamicImport={LazyAdobeAnalytics}
            props={{ adobeConfig: domain.adobeConfig }}
          />
        )}
        {domain.adChoicesConfig && (
          <LazyLoader
            dynamicImport={LazyAdChoices}
            props={{ adChoicesConfig: domain.adChoicesConfig }}
          />
        )}
        {domain.hasVKAnalytics && (
          <LazyLoader dynamicImport={LazyVKAnalytics} />
        )}
        {domain.myTargetAnalyticsID && (
          <LazyLoader
            dynamicImport={LazyMyTargetAnalytics}
            props={{ id: domain.myTargetAnalyticsID }}
          />
        )}
        {domain.dv360AnalyticsID && (
          <LazyLoader
            dynamicImport={LazyDV360Analytics}
            props={{ id: domain.dv360AnalyticsID }}
          />
        )}
        {domain.yandexId && (
          <LazyLoader dynamicImport={LazyYandexAnalytics} props={{ domain }} />
        )}
        <LazyLoader dynamicImport={LazyCustomPixels} props={{ absolutePath }} />
        <LazyLoader dynamicImport={LazyCookieConsentAsync} props={domain} />
        {domain.hotJarID && (
          <LazyLoader
            dynamicImport={LazyHotJar}
            props={{ absolutePath, hotJarID: domain.hotJarID }}
          />
        )}
        <LazyLoader dynamicImport={LazyGlobalFacebookTag} />

        <JSONLDTag
          json={{
            "@context": "http://schema.org",
            "@type": "WebSite",
            name: brand.name,
            url: absolutePath,
          }}
        />
        <JSONLDTag
          json={{
            "@context": "http://schema.org/",
            "@type": "WebPage",
            "@id": `${absolutePath}#webpage`,
            name: pageTitle,
            url: absolutePath,
          }}
        />
        <JSONLDBrand
          absolutePath={absolutePath}
          description={brand.description}
          logo={getSrc(brand.logo.file.fileLocal)}
          name={brand.name}
          sameAs={(socialChannels || []).map(({ url }) => url)}
        />
        <JSONLDTagOrganization />
        <AnalyticsDataLayer
          {...{
            absolutePath,
            title: pageTitle,
            traits,
            pageType,
            domain,
            brand,
            channel,
          }}
        />
        <input
          type="checkbox"
          id="nav-toggle"
          tabIndex="-1"
          ref={navToggleRef}
          onChange={(e) => {
            setUnscrollable(navToggleRef.current.checked);
            if (e.target.checked) {
              trackHamburgerClickedOpen();
            }
          }}
        />
        <Banner {...{ domain }} />
        <div className="z-50 w-full h-full bg-white header-wrapper">
          <header className="items-center max-w-3xl mx-auto lg:flex lg:items-stretch">
            <div data-cy="logo" className="logo-wrapper">
              <Logo homeLink={homeLink} brand={brand} />
            </div>
            <Nav absolutePath={absolutePath} />
            <HamburgerLabel
              navToggleRef={navToggleRef}
              trackHamburgerClickedOpen={trackHamburgerClickedOpen}
            />
          </header>
        </div>
        <div className="relative max-w-3xl mx-auto body-wrapper bg-grey-darkest">
          <Main className={className}>{children}</Main>
          <Footer
            hideDisclaimerMessage={absolutePath.startsWith(
              "https://www.persil.com/uk/dirtisgoodproject"
            )}
            home={home}
          />
          <OneTrustShowSettings {...domain} />
        </div>
      </div>
    </>
  );
};

const Logo = ({ homeLink, brand }) => {
  return (
    <InternalLink
      href={homeLink.path}
      title={homeLink.lexicon.home}
      className="flex mr-auto flex-0 header-logo"
    >
      <GatsbyImage
        image={getImage(brand.logo.file.fileLocal)}
        alt={brand.logo.description}
        className="block"
        // loading="eager" // disabled to avoid the bug where the placeholder never disappeared, resulting in a blurry logo (Chrome desktop v92)
      />
      <span className="sr-only">{homeLink.lexicon.home}</span>
    </InternalLink>
  );
};

const HamburgerLabel = ({ navToggleRef, trackHamburgerClickedOpen }) => {
  return (
    <label
      data-cy="hamburger-menu"
      tabIndex="0"
      htmlFor="nav-toggle"
      className="hamburger lg:hidden"
      aria-controls="nav-list"
      aria-label="Navigation"
      onKeyDown={({ keyCode, which }) => {
        if (13 === (which || keyCode)) {
          navToggleRef.current.checked = !navToggleRef.current.checked;
          if (navToggleRef.current.checked) {
            trackHamburgerClickedOpen();
          }
        }
      }}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="64"
        height="64"
        aria-hidden={true}
      >
        <path
          fill="none"
          d="M16 32h32s11.723-.306 10.75-11c-.25-2.75-1.644-4.971-2.869-7.151C50.728 7.08 42.767 2.569 33.733 2.054 33.159 2.033 32.599 2 32 2 15.432 2 2 15.432 2 32c0 16.566 13.432 30 30 30 16.566 0 30-13.434 30-30C62 15.5 48.5 2 32 2S1.875 15.5 1.875 32"
        />
      </svg>
    </label>
  );
};

Layout.propTypes = {
  absolutePath: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  pageTitle: PropTypes.string.isRequired,
  home: PropTypes.shape({
    brand: PropTypes.object.isRequired,
    domain: PropTypes.object.isRequired,
  }),
  metaDescription: PropTypes.string,
  channel: PropTypes.string,
};

export default Layout;
